import type { vitality as en } from '@/i18n/locales/en/vitality';
import type { Translation } from '@/i18n/types';

export const vitality: Translation<typeof en> = {
  page: {
    title: 'Vitaliteit',
    description: 'Geanonimiseerde, indicatieve welzijnstrends op groepsniveau.',
    noScope: 'Kies een bedrijf om de vitaliteitscijfers te zien.',
    noDepartments: 'Geen enkele afdeling hier heeft statistieken die je mag zien.',
    disclaimer:
      'Alle waarden zijn <strong>indicatieve welzijnsinzichten</strong> en <strong>niet bedoeld als medische diagnose</strong>. Trends worden alleen getoond bij voldoende deelnemers (privacy-by-design).',
  },
  filters: {
    title: 'Filters',
    department: {
      label: 'Afdeling',
    },
    location: {
      label: 'Locatie',
      all: 'Alle locaties',
    },
  },
  badge: {
    measured: 'Gemeten',
    aiDerived: 'AI-inzicht',
  },
  level: {
    good: 'Goed',
    opportunity: 'Kans',
  },
  statusPill: {
    healthy: 'Gezond niveau',
    opportunity: 'Kans om te helpen',
    strongForward: 'Sterk & vooruit',
  },
  trend: {
    flat: 'Geen verandering',
    vsPrevious: 't.o.v. vorige periode',
    vsAverage: 't.o.v. gemiddelde',
    up: 'Vooruit',
    down: 'Iets lager',
    upBad: 'Iets hoger',
    week: 'wk',
  },
  units: {
    hoursMinutes: '{{h}}u {{m}}m',
    average: 'gem. {{value}}',
  },
  status: {
    noData: 'Geen data in deze periode.',
  },
  table: {
    level: 'Niveau',
    trend: 'Trend',
    action: 'Actie',
    actionNone: '—',
    actionLink: 'Afdeling',
    actionLinkTitle: 'Ga naar deze afdeling',
  },
  insight: {
    title: 'Wat vertellen de vitaliteitscijfers?',
    unavailable: 'Nog onvoldoende data voor een samenvatting.',
    vitality: {
      up: 'De vitaliteit stijgt ({{pct}}% {{period}}) bij een deelname van {{participation}}%.',
      down: 'De vitaliteit daalt ({{pct}}% {{period}}) bij een deelname van {{participation}}%.',
      flat: 'De vitaliteit is stabiel ({{pct}}% {{period}}) bij een deelname van {{participation}}%.',
      noParticipation: 'De vitaliteit is stabiel ({{pct}}% {{period}}).',
    },
    period: {
      last7Days: 'de afgelopen 7 dagen',
      last30Days: 'deze maand',
      thisWeek: 'deze week',
      thisMonth: 'deze maand',
      custom: 'in deze periode',
    },
    stress: {
      up: 'Stress loopt op ({{pct}}%)',
      down: 'stress neemt af ({{pct}}%)',
      flat: 'stress blijft gelijk',
    },
    sleep: {
      longer: 'en medewerkers slapen gemiddeld {{minutes}} min langer.',
      shorter: 'en medewerkers slapen gemiddeld {{minutes}} min korter.',
      same: 'en medewerkers slapen ongeveer evenveel.',
    },
    mostActiveDay: '{{day}} is de meest actieve dag.',
  },
  cards: {
    vitalityScore: {
      title: 'Vitaliteitsscore',
      unit: '%',
      info: 'Een samengesteld cijfer op basis van slaap, activiteit, HRV, stress en rusthartslag.',
    },
    sleep: {
      title: 'Slaap',
      unit: '',
      info: 'Gemiddelde slaapduur per nacht.',
    },
    stress: {
      title: 'Stress',
      unit: '%',
      info: 'Gemeten stressniveau; hoe lager, hoe rustiger.',
    },
    recovery: {
      title: 'Herstel',
      unit: '%',
      info: 'Hoe goed het lichaam herstelt, op basis van slaap, HRV en stress.',
    },
    activity: {
      title: 'Activiteit',
      unit: 'stappen/dag',
      info: 'Gemiddeld aantal stappen per dag.',
    },
    hrv: {
      title: 'HRV',
      unit: 'ms',
      info: 'Hartslagvariabiliteit — een indicator voor herstel en veerkracht.',
    },
    restingHeartRate: {
      title: 'Rusthartslag',
      unit: 'bpm',
      info: 'De laagst gemeten hartslag in de gekozen periode, als benadering van de rusthartslag.',
    },
  },
  actions: {
    vitalityScore: {
      good: 'De vitaliteit ontwikkelt zich goed — houd de huidige aanpak vast.',
      opportunity:
        'Er zit ruimte in de vitaliteit — een korte check-in met het team kan duidelijk maken wat er in de weg zit.',
    },
    sleep: {
      good: 'De slaap ziet er gezond uit binnen het team — geen actie nodig.',
      opportunity:
        'De slaap zit onder het streefniveau — overweeg een reminder over een vaste avondroutine en minder schermtijd voor het slapengaan.',
    },
    stress: {
      good: 'Het stressniveau is onder controle — blijf monitoren.',
      opportunity:
        'De stress is verhoogd — een korte check-in over de werkdruk of een herstelgerichte activiteit kan helpen.',
    },
    recovery: {
      good: 'Het herstel is solide — het team veert goed terug na inspanning.',
      opportunity: 'Het herstel blijft achter — kijk of rustdagen en werkdruk in balans zijn.',
    },
    activity: {
      good: 'Het activiteitsniveau is gezond — geen actie nodig.',
      opportunity:
        'De activiteit zit onder het streefniveau — een wandelvergadering of stappenchallenge kan helpen.',
    },
    hrv: {
      good: 'De HRV ziet er gezond uit — geen actie nodig.',
      opportunity:
        'De HRV is lager dan verwacht — dit hangt vaak samen met stress of slechte slaap; bespreek het samen.',
    },
    restingHeartRate: {
      good: 'De rusthartslag zit in een gezond bereik.',
      opportunity:
        'De rusthartslag is verhoogd — in combinatie met stress of minder slaap is dit het signaleren waard.',
    },
  },
};
